// Region / administrative-division lists per country, keyed by ISO 3166-1
// alpha-2 code. Used to render a dynamic "Regija" dropdown in the KYC form:
// when the selected country has a list here, the user picks from it; any
// country not in this map (e.g. "Drugo") falls back to a free-text input.
//
// Values are the first-level administrative subdivisions (ISO 3166-2), in the
// local / commonly-used spelling, matching how Slovenia's 12 statistical
// regions are written elsewhere in the app.

export const REGIONS_BY_ISO = {
  // Slovenia — 12 statistical regions
  SI: [
    'Pomurska', 'Podravska', 'Koroška', 'Savinjska', 'Zasavska', 'Posavska',
    'Jugovzhodna Slovenija', 'Primorsko-notranjska', 'Osrednjeslovenska',
    'Gorenjska', 'Goriška', 'Obalno-kraška'
  ],

  // Austria — 9 states
  AT: [
    'Burgenland', 'Kärnten', 'Niederösterreich', 'Oberösterreich', 'Salzburg',
    'Steiermark', 'Tirol', 'Vorarlberg', 'Wien'
  ],

  // Belgium — 10 provinces + Brussels-Capital
  BE: [
    'Antwerpen', 'Limburg', 'Oost-Vlaanderen', 'Vlaams-Brabant',
    'West-Vlaanderen', 'Henegouwen', 'Luik', 'Luxemburg', 'Namen',
    'Waals-Brabant', 'Brussel'
  ],

  // Bulgaria — 28 provinces (oblasti)
  BG: [
    'Blagoevgrad', 'Burgas', 'Dobrich', 'Gabrovo', 'Haskovo', 'Kardzhali',
    'Kyustendil', 'Lovech', 'Montana', 'Pazardzhik', 'Pernik', 'Pleven',
    'Plovdiv', 'Razgrad', 'Ruse', 'Shumen', 'Silistra', 'Sliven', 'Smolyan',
    'Sofia', 'Sofia-grad', 'Stara Zagora', 'Targovishte', 'Varna',
    'Veliko Tarnovo', 'Vidin', 'Vratsa', 'Yambol'
  ],

  // Bosnia and Herzegovina — entities + Brčko District
  BA: [
    'Federacija Bosne i Hercegovine', 'Republika Srpska', 'Brčko distrikt'
  ],

  // Cyprus — 6 districts
  CY: [
    'Lefkosia', 'Lemesos', 'Larnaka', 'Ammochostos', 'Pafos', 'Keryneia'
  ],

  // Czechia — 14 regions (kraje)
  CZ: [
    'Praha', 'Středočeský', 'Jihočeský', 'Plzeňský', 'Karlovarský', 'Ústecký',
    'Liberecký', 'Královéhradecký', 'Pardubický', 'Kraj Vysočina',
    'Jihomoravský', 'Olomoucký', 'Zlínský', 'Moravskoslezský'
  ],

  // Montenegro — municipalities (opštine)
  ME: [
    'Andrijevica', 'Bar', 'Berane', 'Bijelo Polje', 'Budva', 'Cetinje',
    'Danilovgrad', 'Gusinje', 'Herceg Novi', 'Kolašin', 'Kotor', 'Mojkovac',
    'Nikšić', 'Petnjica', 'Plav', 'Pljevlja', 'Plužine', 'Podgorica', 'Rožaje',
    'Šavnik', 'Tivat', 'Ulcinj', 'Žabljak'
  ],

  // Denmark — 5 regions
  DK: [
    'Hovedstaden', 'Sjælland', 'Syddanmark', 'Midtjylland', 'Nordjylland'
  ],

  // Estonia — 15 counties (maakond)
  EE: [
    'Harjumaa', 'Hiiumaa', 'Ida-Virumaa', 'Järvamaa', 'Jõgevamaa', 'Läänemaa',
    'Lääne-Virumaa', 'Põlvamaa', 'Pärnumaa', 'Raplamaa', 'Saaremaa', 'Tartumaa',
    'Valgamaa', 'Viljandimaa', 'Võrumaa'
  ],

  // Finland — 19 regions (maakunta)
  FI: [
    'Uusimaa', 'Varsinais-Suomi', 'Satakunta', 'Kanta-Häme', 'Pirkanmaa',
    'Päijät-Häme', 'Kymenlaakso', 'Etelä-Karjala', 'Etelä-Savo', 'Pohjois-Savo',
    'Pohjois-Karjala', 'Keski-Suomi', 'Etelä-Pohjanmaa', 'Pohjanmaa',
    'Keski-Pohjanmaa', 'Pohjois-Pohjanmaa', 'Kainuu', 'Lappi', 'Ahvenanmaa'
  ],

  // France — 18 regions (13 metropolitan + 5 overseas)
  FR: [
    'Auvergne-Rhône-Alpes', 'Bourgogne-Franche-Comté', 'Bretagne',
    'Centre-Val de Loire', 'Corse', 'Grand Est', 'Hauts-de-France',
    'Île-de-France', 'Normandie', 'Nouvelle-Aquitaine', 'Occitanie',
    'Pays de la Loire', "Provence-Alpes-Côte d'Azur", 'Guadeloupe', 'Martinique',
    'Guyane', 'La Réunion', 'Mayotte'
  ],

  // Greece — 13 regions
  GR: [
    'Anatoliki Makedonia kai Thraki', 'Kentriki Makedonia', 'Dytiki Makedonia',
    'Ipeiros', 'Thessalia', 'Ionia Nisia', 'Dytiki Ellada', 'Sterea Ellada',
    'Attiki', 'Peloponnisos', 'Voreio Aigaio', 'Notio Aigaio', 'Kriti'
  ],

  // Croatia — 20 counties + City of Zagreb
  HR: [
    'Zagrebačka', 'Krapinsko-zagorska', 'Sisačko-moslavačka', 'Karlovačka',
    'Varaždinska', 'Koprivničko-križevačka', 'Bjelovarsko-bilogorska',
    'Primorsko-goranska', 'Ličko-senjska', 'Virovitičko-podravska',
    'Požeško-slavonska', 'Brodsko-posavska', 'Zadarska', 'Osječko-baranjska',
    'Šibensko-kninska', 'Vukovarsko-srijemska', 'Splitsko-dalmatinska',
    'Istarska', 'Dubrovačko-neretvanska', 'Međimurska', 'Grad Zagreb'
  ],

  // Ireland — 26 traditional counties
  IE: [
    'Carlow', 'Cavan', 'Clare', 'Cork', 'Donegal', 'Dublin', 'Galway', 'Kerry',
    'Kildare', 'Kilkenny', 'Laois', 'Leitrim', 'Limerick', 'Longford', 'Louth',
    'Mayo', 'Meath', 'Monaghan', 'Offaly', 'Roscommon', 'Sligo', 'Tipperary',
    'Waterford', 'Westmeath', 'Wexford', 'Wicklow'
  ],

  // Iceland — 8 regions
  IS: [
    'Höfuðborgarsvæðið', 'Suðurnes', 'Vesturland', 'Vestfirðir',
    'Norðurland vestra', 'Norðurland eystra', 'Austurland', 'Suðurland'
  ],

  // Italy — 20 regions
  IT: [
    'Abruzzo', 'Basilicata', 'Calabria', 'Campania', 'Emilia-Romagna',
    'Friuli-Venezia Giulia', 'Lazio', 'Liguria', 'Lombardia', 'Marche',
    'Molise', 'Piemonte', 'Puglia', 'Sardegna', 'Sicilia', 'Toscana',
    'Trentino-Alto Adige', 'Umbria', "Valle d'Aosta", 'Veneto'
  ],

  // Latvia — 6 planning regions
  LV: [
    'Rīga', 'Pierīga', 'Vidzeme', 'Kurzeme', 'Zemgale', 'Latgale'
  ],

  // Liechtenstein — 11 municipalities
  LI: [
    'Balzers', 'Eschen', 'Gamprin', 'Mauren', 'Planken', 'Ruggell', 'Schaan',
    'Schellenberg', 'Triesen', 'Triesenberg', 'Vaduz'
  ],

  // Lithuania — 10 counties (apskritis)
  LT: [
    'Alytaus apskritis', 'Kauno apskritis', 'Klaipėdos apskritis',
    'Marijampolės apskritis', 'Panevėžio apskritis', 'Šiaulių apskritis',
    'Tauragės apskritis', 'Telšių apskritis', 'Utenos apskritis',
    'Vilniaus apskritis'
  ],

  // Luxembourg — 12 cantons
  LU: [
    'Capellen', 'Clervaux', 'Diekirch', 'Echternach', 'Esch-sur-Alzette',
    'Grevenmacher', 'Luxembourg', 'Mersch', 'Redange', 'Remich', 'Vianden',
    'Wiltz'
  ],

  // Hungary — 19 counties + Budapest
  HU: [
    'Bács-Kiskun', 'Baranya', 'Békés', 'Borsod-Abaúj-Zemplén',
    'Csongrád-Csanád', 'Fejér', 'Győr-Moson-Sopron', 'Hajdú-Bihar', 'Heves',
    'Jász-Nagykun-Szolnok', 'Komárom-Esztergom', 'Nógrád', 'Pest', 'Somogy',
    'Szabolcs-Szatmár-Bereg', 'Tolna', 'Vas', 'Veszprém', 'Zala', 'Budapest'
  ],

  // Malta — 5 regions
  MT: [
    'Gozo', 'Northern', 'Central', 'South Eastern', 'Southern'
  ],

  // Germany — 16 states
  DE: [
    'Baden-Württemberg', 'Bayern', 'Berlin', 'Brandenburg', 'Bremen', 'Hamburg',
    'Hessen', 'Mecklenburg-Vorpommern', 'Niedersachsen', 'Nordrhein-Westfalen',
    'Rheinland-Pfalz', 'Saarland', 'Sachsen', 'Sachsen-Anhalt',
    'Schleswig-Holstein', 'Thüringen'
  ],

  // Netherlands — 12 provinces
  NL: [
    'Drenthe', 'Flevoland', 'Friesland', 'Gelderland', 'Groningen', 'Limburg',
    'Noord-Brabant', 'Noord-Holland', 'Overijssel', 'Utrecht', 'Zeeland',
    'Zuid-Holland'
  ],

  // Norway — 15 counties (fylker)
  NO: [
    'Oslo', 'Rogaland', 'Møre og Romsdal', 'Nordland', 'Østfold', 'Akershus',
    'Buskerud', 'Innlandet', 'Vestfold', 'Telemark', 'Agder', 'Vestland',
    'Trøndelag', 'Troms', 'Finnmark'
  ],

  // Poland — 16 voivodeships
  PL: [
    'Dolnośląskie', 'Kujawsko-Pomorskie', 'Lubelskie', 'Lubuskie', 'Łódzkie',
    'Małopolskie', 'Mazowieckie', 'Opolskie', 'Podkarpackie', 'Podlaskie',
    'Pomorskie', 'Śląskie', 'Świętokrzyskie', 'Warmińsko-Mazurskie',
    'Wielkopolskie', 'Zachodniopomorskie'
  ],

  // Portugal — 18 districts + 2 autonomous regions
  PT: [
    'Aveiro', 'Beja', 'Braga', 'Bragança', 'Castelo Branco', 'Coimbra', 'Évora',
    'Faro', 'Guarda', 'Leiria', 'Lisboa', 'Portalegre', 'Porto', 'Santarém',
    'Setúbal', 'Viana do Castelo', 'Vila Real', 'Viseu', 'Açores', 'Madeira'
  ],

  // Romania — 41 counties + Bucharest
  RO: [
    'Alba', 'Arad', 'Argeș', 'Bacău', 'Bihor', 'Bistrița-Năsăud', 'Botoșani',
    'Brașov', 'Brăila', 'Buzău', 'Caraș-Severin', 'Călărași', 'Cluj',
    'Constanța', 'Covasna', 'Dâmbovița', 'Dolj', 'Galați', 'Giurgiu', 'Gorj',
    'Harghita', 'Hunedoara', 'Ialomița', 'Iași', 'Ilfov', 'Maramureș',
    'Mehedinți', 'Mureș', 'Neamț', 'Olt', 'Prahova', 'Satu Mare', 'Sălaj',
    'Sibiu', 'Suceava', 'Teleorman', 'Timiș', 'Tulcea', 'Vaslui', 'Vâlcea',
    'Vrancea', 'București'
  ],

  // North Macedonia — 8 statistical regions
  MK: [
    'Vardarski', 'Istočen', 'Jugozapaden', 'Jugoistočen', 'Pelagoniski',
    'Pološki', 'Severoistočen', 'Skopski'
  ],

  // Slovakia — 8 regions (kraje)
  SK: [
    'Bratislavský', 'Trnavský', 'Trenčiansky', 'Nitriansky', 'Žilinský',
    'Banskobystrický', 'Prešovský', 'Košický'
  ],

  // Serbia — statistical regions
  RS: [
    'Beograd', 'Vojvodina', 'Šumadija i Zapadna Srbija',
    'Južna i Istočna Srbija'
  ],

  // Spain — 17 autonomous communities + 2 autonomous cities
  ES: [
    'Andalucía', 'Aragón', 'Asturias', 'Illes Balears', 'Canarias', 'Cantabria',
    'Castilla-La Mancha', 'Castilla y León', 'Cataluña', 'Extremadura',
    'Galicia', 'La Rioja', 'Comunidad de Madrid', 'Región de Murcia', 'Navarra',
    'País Vasco', 'Comunitat Valenciana', 'Ceuta', 'Melilla'
  ],

  // Sweden — 21 counties (län)
  SE: [
    'Stockholm', 'Uppsala', 'Södermanland', 'Östergötland', 'Jönköping',
    'Kronoberg', 'Kalmar', 'Gotland', 'Blekinge', 'Skåne', 'Halland',
    'Västra Götaland', 'Värmland', 'Örebro', 'Västmanland', 'Dalarna',
    'Gävleborg', 'Västernorrland', 'Jämtland', 'Västerbotten', 'Norrbotten'
  ],

  // Switzerland — 26 cantons
  CH: [
    'Aargau', 'Appenzell Ausserrhoden', 'Appenzell Innerrhoden',
    'Basel-Landschaft', 'Basel-Stadt', 'Bern', 'Freiburg', 'Genf', 'Glarus',
    'Graubünden', 'Jura', 'Luzern', 'Neuenburg', 'Nidwalden', 'Obwalden',
    'Schaffhausen', 'Schwyz', 'Solothurn', 'St. Gallen', 'Tessin', 'Thurgau',
    'Uri', 'Waadt', 'Wallis', 'Zug', 'Zürich'
  ],

  // United Kingdom — 12 statistical regions (nations + England regions)
  GB: [
    'North East England', 'North West England', 'Yorkshire and the Humber',
    'East Midlands', 'West Midlands', 'East of England', 'London',
    'South East England', 'South West England', 'Scotland', 'Wales',
    'Northern Ireland'
  ],

  // United States — 50 states + District of Columbia
  US: [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
    'Connecticut', 'Delaware', 'District of Columbia', 'Florida', 'Georgia',
    'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
    'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
    'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
    'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota',
    'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island',
    'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
    'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
  ],

  // Japan — 47 prefectures
  JP: [
    'Hokkaido', 'Aomori', 'Iwate', 'Miyagi', 'Akita', 'Yamagata', 'Fukushima',
    'Ibaraki', 'Tochigi', 'Gunma', 'Saitama', 'Chiba', 'Tokyo', 'Kanagawa',
    'Niigata', 'Toyama', 'Ishikawa', 'Fukui', 'Yamanashi', 'Nagano', 'Gifu',
    'Shizuoka', 'Aichi', 'Mie', 'Shiga', 'Kyoto', 'Osaka', 'Hyogo', 'Nara',
    'Wakayama', 'Tottori', 'Shimane', 'Okayama', 'Hiroshima', 'Yamaguchi',
    'Tokushima', 'Kagawa', 'Ehime', 'Kochi', 'Fukuoka', 'Saga', 'Nagasaki',
    'Kumamoto', 'Oita', 'Miyazaki', 'Kagoshima', 'Okinawa'
  ],

  // United Arab Emirates — 7 emirates
  AE: [
    'Abu Dhabi', 'Dubai', 'Sharjah', 'Ajman', 'Umm Al Quwain',
    'Ras Al Khaimah', 'Fujairah'
  ],

  // Turkey — 81 provinces
  TR: [
    'Adana', 'Adıyaman', 'Afyonkarahisar', 'Ağrı', 'Aksaray', 'Amasya',
    'Ankara', 'Antalya', 'Ardahan', 'Artvin', 'Aydın', 'Balıkesir', 'Bartın',
    'Batman', 'Bayburt', 'Bilecik', 'Bingöl', 'Bitlis', 'Bolu', 'Burdur',
    'Bursa', 'Çanakkale', 'Çankırı', 'Çorum', 'Denizli', 'Diyarbakır', 'Düzce',
    'Edirne', 'Elazığ', 'Erzincan', 'Erzurum', 'Eskişehir', 'Gaziantep',
    'Giresun', 'Gümüşhane', 'Hakkâri', 'Hatay', 'Iğdır', 'Isparta', 'İstanbul',
    'İzmir', 'Kahramanmaraş', 'Karabük', 'Karaman', 'Kars', 'Kastamonu',
    'Kayseri', 'Kilis', 'Kırıkkale', 'Kırklareli', 'Kırşehir', 'Kocaeli',
    'Konya', 'Kütahya', 'Malatya', 'Manisa', 'Mardin', 'Mersin', 'Muğla',
    'Muş', 'Nevşehir', 'Niğde', 'Ordu', 'Osmaniye', 'Rize', 'Sakarya',
    'Samsun', 'Şanlıurfa', 'Siirt', 'Sinop', 'Sivas', 'Şırnak', 'Tekirdağ',
    'Tokat', 'Trabzon', 'Tunceli', 'Uşak', 'Van', 'Yalova', 'Yozgat',
    'Zonguldak'
  ],

  // South Africa — 9 provinces
  ZA: [
    'Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal', 'Limpopo',
    'Mpumalanga', 'North West', 'Northern Cape', 'Western Cape'
  ],

  // Brazil — 26 states + Federal District
  BR: [
    'Acre', 'Alagoas', 'Amapá', 'Amazonas', 'Bahia', 'Ceará',
    'Distrito Federal', 'Espírito Santo', 'Goiás', 'Maranhão', 'Mato Grosso',
    'Mato Grosso do Sul', 'Minas Gerais', 'Pará', 'Paraíba', 'Paraná',
    'Pernambuco', 'Piauí', 'Rio de Janeiro', 'Rio Grande do Norte',
    'Rio Grande do Sul', 'Rondônia', 'Roraima', 'Santa Catarina', 'São Paulo',
    'Sergipe', 'Tocantins'
  ],

  // Russia — 8 federal districts
  RU: [
    'Central Federal District', 'Northwestern Federal District',
    'Southern Federal District', 'North Caucasian Federal District',
    'Volga Federal District', 'Ural Federal District',
    'Siberian Federal District', 'Far Eastern Federal District'
  ],

  // Ukraine — 24 oblasts + Kyiv city
  UA: [
    'Cherkasy', 'Chernihiv', 'Chernivtsi', 'Dnipropetrovsk', 'Donetsk',
    'Ivano-Frankivsk', 'Kharkiv', 'Kherson', 'Khmelnytskyi', 'Kirovohrad',
    'Kyiv Oblast', 'Luhansk', 'Lviv', 'Mykolaiv', 'Odesa', 'Poltava', 'Rivne',
    'Sumy', 'Ternopil', 'Vinnytsia', 'Volyn', 'Zakarpattia', 'Zaporizhzhia',
    'Zhytomyr', 'Kyiv'
  ]
};

// Returns the region list for an ISO2 code, or null when the country has no
// predefined list (caller should render a free-text input instead).
export function getRegions(iso2) {
  if (!iso2) return null;
  return REGIONS_BY_ISO[iso2] || null;
}
